import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../services/api';
import { useEnvironment } from '../hooks/useEnvironment';
import {
  ServerIcon,
  CircleStackIcon,
  CpuChipIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';

export const SystemStatus: React.FC = () => {
  const { isDevelopment } = useEnvironment();
  
  const { data: health, isLoading, error } = useQuery({
    queryKey: ['health'],
    queryFn: api.getHealth,
    refetchInterval: 30000, // Aggiorna ogni 30 secondi
  });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'warning':
        return <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />;
      case 'unhealthy':
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      default:
        return <ExclamationTriangleIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy':
        return 'text-green-700 bg-green-100';
      case 'warning':
        return 'text-yellow-700 bg-yellow-100';
      case 'unhealthy':
        return 'text-red-700 bg-red-100';
      default:
        return 'text-gray-700 bg-gray-100';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'healthy':
        return 'Operativo';
      case 'warning':
        return 'Attenzione';
      case 'unhealthy':
        return 'Non Operativo';
      default:
        return 'Sconosciuto';
    }
  };

  const formatUptime = (seconds: number) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (days > 0) {
      return `${days}g ${hours}h ${minutes}m`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Caricamento stato del sistema...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <XCircleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Errore di Connessione</h3>
          <p className="text-gray-600">Impossibile recuperare lo stato del sistema</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900">
          Stato del Sistema
        </h1>
        <p className="mt-2 text-gray-600">
          Monitoraggio in tempo reale dei servizi
        </p>
      </div>

      {/* Overall Status */}
      <div className="card">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
              <ServerIcon className="h-6 w-6 text-primary-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Stato Generale</h3>
              <p className="text-sm text-gray-600">
                Ultimo aggiornamento: {new Date(health?.timestamp || Date.now()).toLocaleString('it-IT')}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            {getStatusIcon(health?.status || 'unknown')}
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(health?.status || 'unknown')}`}>
              {getStatusText(health?.status || 'unknown')}
            </span>
          </div>
        </div>
      </div>

      {/* System Info */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Uptime */}
        <div className="card">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-accent-100 rounded-lg flex items-center justify-center">
              <ClockIcon className="h-6 w-6 text-accent-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Uptime</h3>
              <p className="text-2xl font-bold text-gray-900">
                {formatUptime(health?.uptime || 0)}
              </p>
            </div>
          </div>
        </div>

        {/* Response Time */}
        <div className="card">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-success-100 rounded-lg flex items-center justify-center">
              <CpuChipIcon className="h-6 w-6 text-success-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Tempo di Risposta</h3>
              <p className="text-2xl font-bold text-gray-900">
                {health?.responseTime || 0}ms
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Services Status */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Stato dei Servizi</h3>
        <div className="space-y-4">
          {health?.services && Object.entries(health.services).map(([serviceName, service]: [string, any]) => (
            <div key={serviceName} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center space-x-4">
                <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                  {serviceName === 'database' ? (
                    <CircleStackIcon className="h-5 w-5 text-blue-600" />
                  ) : serviceName === 'api' ? (
                    <ServerIcon className="h-5 w-5 text-green-600" />
                  ) : (
                    <CpuChipIcon className="h-5 w-5 text-purple-600" />
                  )}
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 capitalize">
                    {serviceName === 'database' ? 'Database' : 
                     serviceName === 'api' ? 'API Server' : 
                     serviceName === 'memory' ? 'Memoria' : serviceName}
                  </h4>
                  <p className="text-sm text-gray-600">{service.message}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                {getStatusIcon(service.status)}
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(service.status)}`}>
                  {getStatusText(service.status)}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Memory Usage */}
      {health?.services?.memory && (
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Utilizzo Memoria</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-700">Utilizzo Attuale</span>
              <span className="text-sm font-medium text-gray-900">
                {health.services.memory.usage?.used || 0}GB / {health.services.memory.usage?.total || 0}GB
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className={`h-2 rounded-full ${
                  (health.services.memory.usage?.percentage || 0) > 80 ? 'bg-red-500' :
                  (health.services.memory.usage?.percentage || 0) > 60 ? 'bg-yellow-500' : 'bg-green-500'
                }`}
                style={{ width: `${health.services.memory.usage?.percentage || 0}%` }}
              ></div>
            </div>
            <div className="flex justify-between text-xs text-gray-500">
              <span>0%</span>
              <span className="font-medium">{health.services.memory.usage?.percentage || 0}%</span>
              <span>100%</span>
            </div>
          </div>
        </div>
      )}

      {/* Debug Info */}
      {isDevelopment && (
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Informazioni Debug</h3>
          <div className="bg-gray-100 rounded-lg p-4">
            <pre className="text-xs text-gray-700 overflow-x-auto">
              {JSON.stringify(health, null, 2)}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
};
